import { DB } from "./deviceStorage";
// eslint-disable-next-line no-unused-vars
import { makeCreateMeta } from "./meta";
import { createClientId } from "./formatters";
import { normalizeUiPreferences } from "./theme";
import { stableJson } from "./syncUtils";
import { pushGlobal, pullGlobal } from "../sync/collectionApi";

const USER_DELETION_LOG_KEY = "fz:userDeletionLog";
const DEFAULT_ADMIN_PASSWORD = "admin1979";

function buildDeletionTombstones(entries) {
  const out = { accountIds: new Set(), linkedAccountIds: new Set(), employeeIds: new Set(), usernames: new Set() };
  for (const item of Array.isArray(entries) ? entries : []) {
    const accountId      = String(item?.accountId      || "").trim();
    const linkedAccountId= String(item?.linkedAccountId|| "").trim();
    const employeeId     = String(item?.employeeId     || "").trim();
    const username       = String(item?.username       || "").trim().toLowerCase();
    if (accountId)       out.accountIds.add(accountId);
    if (linkedAccountId) out.linkedAccountIds.add(linkedAccountId);
    if (employeeId)      out.employeeIds.add(employeeId);
    if (username)        out.usernames.add(username);
  }
  return out;
}

function pruneDeletedAccounts(accounts, deletionLog) {
  const tomb = buildDeletionTombstones(deletionLog);
  return (Array.isArray(accounts) ? accounts : []).filter(acc => {
    const id       = String(acc?.id       || "").trim();
    const username = String(acc?.username || "").trim().toLowerCase();
    const isDefaultAdmin = username === "admin" && String(acc?.role || "") === "admin";
    if (isDefaultAdmin) return true;
    if (id && (tomb.accountIds.has(id) || tomb.linkedAccountIds.has(id))) return false;
    if (username && tomb.usernames.has(username)) return false;
    return true;
  });
}

function pruneDeletedEmployees(list, deletionLog) {
  const tomb = buildDeletionTombstones(deletionLog);
  return (Array.isArray(list) ? list : []).filter(emp => {
    const id              = String(emp?.id              || "").trim();
    const linkedAccountId = String(emp?.linkedAccountId || "").trim();
    const username        = String(emp?.username        || "").trim().toLowerCase();
    if (id && tomb.employeeIds.has(id)) return false;
    if (linkedAccountId && (tomb.accountIds.has(linkedAccountId) || tomb.linkedAccountIds.has(linkedAccountId))) return false;
    if (username && tomb.usernames.has(username)) return false;
    return !(emp?._deleted || emp?.deletedAt || emp?.deleted_at);
  });
}

async function getUserDeletionLog(userName) {
  try {
    const local    = await DB.get(USER_DELETION_LOG_KEY);
    const localArr = Array.isArray(local) ? local : [];
    try {
      const srv = await pullGlobal(USER_DELETION_LOG_KEY, userName);
      if (Array.isArray(srv?.data)) {
        const merged = [...srv.data, ...localArr]
          .filter(Boolean)
          .reduce((acc, item) => {
            const key = String(item?.id || "").trim() || JSON.stringify(item);
            if (!acc.map.has(key)) acc.map.set(key, item);
            else acc.map.set(key, { ...acc.map.get(key), ...item });
            return acc;
          }, { map: new Map() }).map;
        const out = [...merged.values()].sort((a,b)=>String(b?.deletedAt||"").localeCompare(String(a?.deletedAt||"")));
        await DB.set(USER_DELETION_LOG_KEY, out);
        return out;
      }
    } catch(_) {}
    return localArr.sort((a,b)=>String(b?.deletedAt||"").localeCompare(String(a?.deletedAt||"")));
  } catch(_) {
    return [];
  }
}

function readAdminPasswordOverride() {
  try {
    const raw = localStorage.getItem("auth:admin:passwordOverride");
    return typeof raw === "string" && raw.trim() ? raw : "";
  } catch(_) {
    return "";
  }
}

function writeAdminPasswordOverride(password) {
  try {
    const pw = String(password || "").trim();
    if (pw) {
      localStorage.setItem("auth:admin:passwordOverride", pw);
      localStorage.setItem("auth:admin:defaultBlocked", pw === DEFAULT_ADMIN_PASSWORD ? "0" : "1");
    }
  } catch(_) {}
}

function readAdminDefaultBlocked() {
  try {
    return localStorage.getItem("auth:admin:defaultBlocked") === "1";
  } catch(_) {
    return false;
  }
}

async function persistAdminCredentialState(accounts) {
  try {
    const list = Array.isArray(accounts) ? accounts : [];
    const admin = list.find(acc => String(acc?.username || "").trim().toLowerCase() === "admin");
    if (!admin) return;
    await DB.set("auth:admin:credential", {
      username: "admin",
      password: String(admin?.password || "").trim() || DEFAULT_ADMIN_PASSWORD,
      defaultPasswordBlocked: admin?.defaultPasswordBlocked === true,
      updatedAt: admin?.updatedAt || admin?.createdAt || new Date().toISOString(),
    });
  } catch(_) {}
}

async function getPersistedAdminCredentialState() {
  try {
    const saved = await DB.get("auth:admin:credential");
    if (saved && typeof saved === "object") return saved;
  } catch(_) {}
  return null;
}

function splitPersonNameParts(value){
  const full = String(value || "").trim().replace(/\s+/g, " ");
  if (!full) return { firstName:"", lastName:"" };
  const parts = full.split(" ").filter(Boolean);
  return { firstName: parts[0] || "", lastName: parts.slice(1).join(" ") };
}

function buildPersonDisplayName(firstName, lastName, fallbackName = "", username = "", role = "solicitante"){
  const first = String(firstName || "").trim();
  const last = String(lastName || "").trim();
  const composed = [first, last].filter(Boolean).join(" ").trim();
  if (composed) return composed;
  const fallback = String(fallbackName || "").trim();
  if (fallback) return fallback;
  return username === "admin" || String(role || "").trim().toLowerCase() === "admin" ? "Administrador" : (String(username || "").trim() || "Usuário");
}

function normalizeLinkUsername(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, ".")
    .replace(/\.{2,}/g, ".")
    .replace(/^\.+|\.+$/g, "")
    .slice(0, 40);
}

function buildUniqueUsername(baseValue, existingAccounts = [], reservedIds = []) {
  const base = normalizeLinkUsername(baseValue) || "colaborador";
  const taken = new Set((Array.isArray(existingAccounts) ? existingAccounts : [])
    .filter(acc => !reservedIds.includes(String(acc?.id || "")))
    .map(acc => String(acc?.username || "").trim().toLowerCase())
    .filter(Boolean));
  if (!taken.has(base)) return base;
  let idx = 2;
  while (taken.has(`${base}.${idx}`)) idx += 1;
  return `${base}.${idx}`;
}

function buildTemporaryAccessPassword(seed = "") {
  const raw = String(seed || createClientId("tmp"))
    .replace(/[^a-zA-Z0-9]/g, "")
    .slice(-8)
    .padStart(8, "7");
  return `Fz${raw}`;
}


function dedupeEmployeeLinkOptions(list = []) {
  const deduped = [];
  const seenById = new Set();
  const seenByLabel = new Set();
  for (const rawItem of Array.isArray(list) ? list : []) {
    const item = rawItem && typeof rawItem === "object" ? rawItem : {};
    const employeeId = String(item.employeeId || item.id || "").trim();
    const username = String(item.username || "").trim().toLowerCase();
    const farmId = String(item.farmId || "").trim();
    const farmName = String(item.farmName || "").trim().toLowerCase();
    const name = String(item.name || "").trim().toLowerCase();
    const primaryKey = employeeId || `${username}::${farmId || farmName}::${name}`;
    const labelKey = `${name}::${username}::${farmId || farmName}`;
    if (!primaryKey) continue;
    if (seenById.has(primaryKey)) continue;
    if (labelKey && seenByLabel.has(labelKey)) continue;
    seenById.add(primaryKey);
    if (labelKey) seenByLabel.add(labelKey);
    deduped.push(item);
  }
  return deduped;
}

function normalizeAuthAccount(acc, actor = "system") {
  const base = acc && typeof acc === "object" ? { ...acc } : {};
  const now = new Date().toISOString();
  const username = String(base?.username || "").trim().toLowerCase();
  const isCanonicalAdmin = username === "admin";
  const createdAt = String(base?.createdAt || "").trim() || now;
  const updatedAt = String(base?.updatedAt || "").trim() || createdAt || now;
  const nameParts = splitPersonNameParts(base?.name || "");
  const firstName = String(base?.firstName || nameParts.firstName || "").trim();
  const lastName = String(base?.lastName || nameParts.lastName || "").trim();
  const displayName = buildPersonDisplayName(firstName, lastName, base?.name || "", username, base?.role || "solicitante");
  const normalizedRole = String(base?.role || "solicitante").trim() || "solicitante";
  const roleIsAdmin = normalizedRole.toLowerCase() === "admin";
  return {
    ...base,
    id: String(base?.id || "").trim() || (isCanonicalAdmin ? "acc_admin" : createClientId("acc")),
    username,
    password: String(base?.password || "").trim(),
    firstName,
    lastName,
    name: displayName,
    role: normalizedRole,
    employeeId: String(base?.employeeId || base?.linkedEmployeeId || "").trim(),
    linkedEmployeeId: String(base?.linkedEmployeeId || base?.employeeId || "").trim(),
    linkedAccountId: String(base?.linkedAccountId || "").trim(),
    userId: String(base?.userId || "").trim(),
    email: String(base?.email || "").trim(),
    phone: String(base?.phone || "").trim(),
    collabLevel: Number(base?.collabLevel || 1) || 1,
    costCenterPermissions: (base?.costCenterPermissions && typeof base.costCenterPermissions === "object") ? base.costCenterPermissions : undefined,
    active: roleIsAdmin ? true : base?.active !== false,
    farmIds: Array.isArray(base?.farmIds) ? [...new Set(base.farmIds.filter(Boolean))] : [],
    allowedModules: Array.isArray(base?.allowedModules) ? [...new Set(base.allowedModules.filter(Boolean))] : [],
    allowedCards: Array.isArray(base?.allowedCards) ? [...new Set(base.allowedCards.filter(Boolean))] : [],
    modulePermissions: (base?.modulePermissions && typeof base.modulePermissions === "object") ? base.modulePermissions : undefined,
    canManagePurchases: normalizedRole.toLowerCase() === "admin" || normalizedRole.toLowerCase() === "gestor"
      ? true
      : !!base?.canManagePurchases,
    canApprovePurchaseWithoutQuote: normalizedRole.toLowerCase() === "admin"
      ? true
      : !!base?.canApprovePurchaseWithoutQuote,
    receivePurchaseNotifications: normalizedRole.toLowerCase() === "admin"
      ? true
      : !!base?.receivePurchaseNotifications,
    uiPreferences: normalizeUiPreferences(base?.uiPreferences),
    defaultPasswordBlocked: base?.defaultPasswordBlocked === true,
    mustChangePassword: base?.mustChangePassword === true,
    temporaryPassword: base?.temporaryPassword === true,
    createdAt,
    updatedAt,
    updatedBy: String(base?.updatedBy || actor || "system").trim() || "system",
  };
}

function normalizeAuthAccountsCollection(accounts, actor = "system") {
  const list = Array.isArray(accounts) ? accounts : [];
  const normalized = list
    .map(acc => normalizeAuthAccount(acc, actor))
    .filter(acc => acc.username || acc.id);
  const merged = [];
  const byId = new Map();
  const byUsername = new Map();
  for (const acc of normalized) {
    const idKey = String(acc?.id || "").trim();
    const userKey = String(acc?.username || "").trim().toLowerCase();
    let idx = -1;
    if (idKey && byId.has(idKey)) idx = byId.get(idKey);
    if (idx < 0 && userKey && byUsername.has(userKey)) idx = byUsername.get(userKey);
    if (idx >= 0) {
      const chosen = choosePreferredAccount(merged[idx], acc);
      merged[idx] = normalizeAuthAccount(chosen, actor);
    } else {
      merged.push(acc);
      idx = merged.length - 1;
    }
    const finalAcc = merged[idx];
    const finalIdKey = String(finalAcc?.id || "").trim();
    const finalUserKey = String(finalAcc?.username || "").trim().toLowerCase();
    if (finalIdKey) byId.set(finalIdKey, idx);
    if (finalUserKey) byUsername.set(finalUserKey, idx);
  }
  return merged;
}

function ensureDefaultAdminAccount(accounts, actor = "system") {
  const list = normalizeAuthAccountsCollection(accounts, actor);
  const adminPasswordOverride = readAdminPasswordOverride();
  const canonicalAdmin = {
    id: "acc_admin",
    username: "admin",
    password: DEFAULT_ADMIN_PASSWORD,
    name: "Administrador",
    role: "admin",
    active: true,
    farmIds: [],
    allowedModules: [],
    allowedCards: [],
    canManagePurchases: true,
    canApprovePurchaseWithoutQuote: true,
    receivePurchaseNotifications: true,
    uiPreferences: normalizeUiPreferences(),
    defaultPasswordBlocked: readAdminDefaultBlocked() || (adminPasswordOverride && adminPasswordOverride !== DEFAULT_ADMIN_PASSWORD),
  };
  const adminIdx = list.findIndex(acc => String(acc?.username || "").trim().toLowerCase() === "admin");
  if (adminIdx >= 0) {
    const current = normalizeAuthAccount(list[adminIdx], actor);
    const rawCurrentPassword = typeof current?.password === "string" ? current.password.trim() : "";
    const currentPassword = rawCurrentPassword;
    const overridePassword = typeof adminPasswordOverride === "string" ? adminPasswordOverride.trim() : "";
    const preservedPassword = currentPassword || overridePassword || canonicalAdmin.password;
    const blockedDefaultPassword = current?.defaultPasswordBlocked === true || readAdminDefaultBlocked() || preservedPassword !== DEFAULT_ADMIN_PASSWORD;
    list[adminIdx] = normalizeAuthAccount({
      ...current,
      ...canonicalAdmin,
      id: current?.id || canonicalAdmin.id,
      username: "admin",
      password: preservedPassword,
      name: current?.name || canonicalAdmin.name,
      role: "admin",
      active: current?.active !== false,
      farmIds: Array.isArray(current?.farmIds) ? current.farmIds : [],
      allowedModules: Array.isArray(current?.allowedModules) ? current.allowedModules : [],
      allowedCards: Array.isArray(current?.allowedCards) ? current.allowedCards : [],
      canManagePurchases: true,
      canApprovePurchaseWithoutQuote: true,
      receivePurchaseNotifications: true,
      defaultPasswordBlocked: blockedDefaultPassword,
      createdAt: current?.createdAt || canonicalAdmin.createdAt,
      updatedAt: current?.updatedAt || canonicalAdmin.updatedAt,
      updatedBy: current?.updatedBy || actor,
    }, actor);
    return normalizeAuthAccountsCollection(list, actor);
  }
  return normalizeAuthAccountsCollection([{ ...canonicalAdmin, password: adminPasswordOverride || canonicalAdmin.password }, ...list], actor);
}

function accountMergeKey(acc) {
  const id = String(acc?.id ?? "").trim();
  if (id) return `i:${id}`;
  const username = String(acc?.username || "").trim().toLowerCase();
  if (username) return `u:${username}`;
  return `fallback:${stableJson(acc || {})}`;
}

function choosePreferredAccount(localAcc, serverAcc) {
  if (!localAcc) return serverAcc;
  if (!serverAcc) return localAcc;

  const localTs = String(localAcc?.updatedAt || localAcc?.createdAt || "").trim();
  const serverTs = String(serverAcc?.updatedAt || serverAcc?.createdAt || "").trim();
  if (localTs && serverTs && localTs !== serverTs) {
    return localTs >= serverTs ? localAcc : serverAcc;
  }

  const isAdmin = (acc) => String(acc?.username || "").trim().toLowerCase() === "admin" || String(acc?.role || "").trim().toLowerCase() === "admin";
  if (isAdmin(localAcc) || isAdmin(serverAcc)) {
    const localPw = String(localAcc?.password || "").trim();
    const serverPw = String(serverAcc?.password || "").trim();
    if (localPw && serverPw && localPw !== serverPw) {
      const localIsDefault = localPw === DEFAULT_ADMIN_PASSWORD;
      const serverIsDefault = serverPw === DEFAULT_ADMIN_PASSWORD;
      if (!localIsDefault && serverIsDefault) return localAcc;
      if (!serverIsDefault && localIsDefault) return serverAcc;
    }
  }

  return localAcc || serverAcc;
}

function mergeAuthAccounts(serverAccounts, localAccounts) {
  const srvArr = Array.isArray(serverAccounts) ? serverAccounts : [];
  const locArr = Array.isArray(localAccounts) ? localAccounts : [];
  const merged = [];
  const keys = new Set([
    ...srvArr.map(accountMergeKey),
    ...locArr.map(accountMergeKey),
  ]);
  for (const key of keys) {
    const srvItem = srvArr.find(item => accountMergeKey(item) === key);
    const locItem = locArr.find(item => accountMergeKey(item) === key);
    merged.push(choosePreferredAccount(locItem, srvItem));
  }
  return ensureDefaultAdminAccount(merged, "merge-auth");
}

async function getAccounts() {
  try {
    const local = await DB.get("auth:accounts");
    const deletionLog = await DB.get(USER_DELETION_LOG_KEY);
    const pruned = pruneDeletedAccounts(local, deletionLog);
    const ensured = ensureDefaultAdminAccount(pruned, "get-accounts");
    const onlySeedAdmin = Array.isArray(ensured) && ensured.length === 1 && String(ensured?.[0]?.username || "").trim().toLowerCase() === "admin";
    if (Array.isArray(ensured) && ensured.length > 0 && (!navigator.onLine || !onlySeedAdmin)) {
      if (JSON.stringify(local || []) !== JSON.stringify(ensured)) {
        await DB.set("auth:accounts", ensured);
      }
      await persistAdminCredentialState(ensured);
      return ensured;
    }
    if (navigator.onLine) {
      try {
        const srv = await pullGlobal("auth:accounts", "get-accounts-bootstrap");
        const remote = pruneDeletedAccounts(Array.isArray(srv?.data) ? srv.data : [], deletionLog);
        const hydrated = Array.isArray(remote) && remote.length > 0 ? ensureDefaultAdminAccount(remote, "get-accounts-remote") : [];
        if (hydrated.length > 0) {
          await DB.set("auth:accounts", hydrated);
          if (srv?.version != null) await DB.set("auth:accounts:_ver", Number(srv.version || 0));
          await persistAdminCredentialState(hydrated);
          return hydrated;
        }
      } catch (_) {}
      return [];
    }
    const seed = ensureDefaultAdminAccount([], "get-accounts-seed");
    await DB.set("auth:accounts", seed);
    await persistAdminCredentialState(seed);
    return seed;
  } catch(e) {
    if (navigator.onLine) {
      try {
        const deletionLog = await DB.get(USER_DELETION_LOG_KEY);
        const srv = await pullGlobal("auth:accounts", "get-accounts-bootstrap-fallback");
        const remote = pruneDeletedAccounts(Array.isArray(srv?.data) ? srv.data : [], deletionLog);
        const hydrated = Array.isArray(remote) && remote.length > 0 ? ensureDefaultAdminAccount(remote, "get-accounts-remote") : [];
        if (hydrated.length > 0) {
          await DB.set("auth:accounts", hydrated);
          if (srv?.version != null) await DB.set("auth:accounts:_ver", Number(srv.version || 0));
          await persistAdminCredentialState(hydrated);
          return hydrated;
        }
      } catch (_) {}
      return [];
    }
    const seed = ensureDefaultAdminAccount([], "get-accounts-seed");
    try { await DB.set("auth:accounts", seed); } catch(_) {}
    try { await persistAdminCredentialState(seed); } catch(_) {}
    return seed;
  }
}

async function saveAccounts(accounts) {
  const deletionLog = await DB.get(USER_DELETION_LOG_KEY);
  const pruned = pruneDeletedAccounts(accounts, deletionLog);
  const ensured = ensureDefaultAdminAccount(pruned, "save-accounts");
  await DB.set("auth:accounts", ensured);
  await persistAdminCredentialState(ensured);
}

async function ensureAdminAccountAvailable(reason="system") {
  const current = await getAccounts();
  const ensured = ensureDefaultAdminAccount(current, reason);
  await persistAdminCredentialState(ensured);
  if (JSON.stringify(current || []) !== JSON.stringify(ensured)) {
    await DB.set("auth:accounts", ensured);
    try { window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: "auth:accounts", source: reason } })); } catch(_) {}
    try {
      // eslint-disable-next-line no-unused-vars
      const currentVersion = (await DB.get("auth:accounts:_ver")) || 0;
      const onlyAccount = ensured[0] || {};
      const hasOnlyDefaultAdmin = ensured.length === 1
        && (String(onlyAccount?.username || "").trim().toLowerCase() === "admin"
          || String(onlyAccount?.id || onlyAccount?.clientId || "").trim().toLowerCase() === "acc_admin")
        && String(onlyAccount?.role || "admin").trim().toLowerCase() === "admin"
        && !onlyAccount?.defaultPasswordBlocked;
      if (!hasOnlyDefaultAdmin) {
        const res = await pushGlobal("auth:accounts", ensured, reason);
        if (res?.version) await DB.set("auth:accounts:_ver", res.version);
      }
    } catch(_) {}
  }
  return ensured;
}


export {
  readAdminPasswordOverride, writeAdminPasswordOverride, readAdminDefaultBlocked,
  persistAdminCredentialState, getPersistedAdminCredentialState,
  splitPersonNameParts, buildPersonDisplayName, normalizeLinkUsername,
  buildUniqueUsername, buildTemporaryAccessPassword, dedupeEmployeeLinkOptions,
  normalizeAuthAccount, normalizeAuthAccountsCollection, ensureDefaultAdminAccount,
  accountMergeKey, choosePreferredAccount, mergeAuthAccounts,
  getAccounts, saveAccounts, ensureAdminAccountAvailable,
  USER_DELETION_LOG_KEY, buildDeletionTombstones,
  pruneDeletedAccounts, pruneDeletedEmployees, getUserDeletionLog,
};
